/**
 * Компонент выбора диапазона дат с пресетами
 * Дата: 29.01.2026
 * Назначение: Фильтрация пациентов по периоду создания
 * Связан с: TODO-021a (mass_binding_enhancements)
 */
import { useState } from 'react';
import { startOfDay, endOfDay, subDays, subMonths, format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { cn } from '@/utils/cn';
import { Button } from './Button';

interface DateRangePickerProps {
  from: Date | null;
  to: Date | null;
  onChange: (from: Date | null, to: Date | null) => void;
  presets?: { label: string; from: Date; to: Date }[];
  className?: string;
  testId?: string;
}

const defaultPresets = [
  { 
    label: 'Сегодня', 
    from: startOfDay(new Date()), 
    to: endOfDay(new Date()) 
  },
  { 
    label: 'Вчера', 
    from: startOfDay(subDays(new Date(), 1)), 
    to: endOfDay(subDays(new Date(), 1)) 
  },
  { 
    label: 'Неделя', 
    from: startOfDay(subDays(new Date(), 7)), 
    to: endOfDay(new Date()) 
  },
  { 
    label: 'Месяц', 
    from: startOfDay(subMonths(new Date(), 1)), 
    to: endOfDay(new Date()) 
  },
];

export function DateRangePicker({ 
  from, 
  to, 
  onChange, 
  presets = defaultPresets,
  className,
  testId
}: DateRangePickerProps) {
  const [showCustom, setShowCustom] = useState(false);
  
  const handlePresetClick = (preset: { from: Date; to: Date }) => {
    onChange(preset.from, preset.to);
    setShowCustom(false);
  };

  const handleFromChange = (value: string) => {
    const date = value ? new Date(value) : null;
    onChange(date ? startOfDay(date) : null, to);
  };

  const handleToChange = (value: string) => {
    const date = value ? new Date(value) : null;
    onChange(from, date ? endOfDay(date) : null);
  };

  const isPresetActive = (preset: { from: Date; to: Date }) => {
    if (!from || !to) return false;
    return (
      format(from, 'yyyy-MM-dd') === format(preset.from, 'yyyy-MM-dd') &&
      format(to, 'yyyy-MM-dd') === format(preset.to, 'yyyy-MM-dd')
    );
  };

  return (
    <div className={cn('flex flex-col gap-3', className)} data-testid={testId}>
      {/* Пресеты */}
      <div className="flex flex-wrap gap-2">
        {presets.map((preset, idx) => (
          <Button
            key={idx}
            variant={isPresetActive(preset) ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => handlePresetClick(preset)}
            className={cn(
              isPresetActive(preset) && 'bg-primary-100 text-primary-700 hover:bg-primary-200'
            )}
          >
            {preset.label}
          </Button>
        ))}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowCustom(!showCustom)}
        >
          {showCustom ? 'Скрыть' : 'Выбрать даты'}
        </Button>
      </div>

      {/* Кастомный выбор дат */}
      {showCustom && (
        <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
          <div className="flex items-center gap-2">
            <label className="text-sm text-neutral-600">От:</label>
            <input
              type="date"
              value={from ? format(from, 'yyyy-MM-dd') : ''}
              onChange={(e) => handleFromChange(e.target.value)}
              className="px-3 py-1.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
            />
          </div>
          <span className="text-neutral-400">—</span>
          <div className="flex items-center gap-2">
            <label className="text-sm text-neutral-600">До:</label>
            <input
              type="date"
              value={to ? format(to, 'yyyy-MM-dd') : ''}
              onChange={(e) => handleToChange(e.target.value)}
              className="px-3 py-1.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
            />
          </div>
          {(from || to) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(null, null)}
            >
              Сбросить
            </Button>
          )}
        </div>
      )}

      {/* Отображение выбранного диапазона */}
      {from && to && (
        <div className="text-sm text-neutral-600">
          Выбрано: {format(from, 'dd.MM.yyyy', { locale: ru })} — {format(to, 'dd.MM.yyyy', { locale: ru })}
        </div>
      )}
    </div>
  );
}
